export type TimeRange = { open: string; close: string };
export type Hours = Record<string, TimeRange[]>;

export const DAYS_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export function getTimeInMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

function getCurrentDayIndex(): number {
  return new Date().getDay();
}

export type OpeningStatus =
  | { status: 'open'; closeTime: string }
  | { status: 'closed'; openDay: 'today' | 'tomorrow' | string; openTime: string };

export function getOpeningStatus(hours: Hours | undefined | null): OpeningStatus | null {
  if (!hours || Object.keys(hours).length === 0) {
    return null;
  }

  const now = new Date();
  const currentMinutes = getTimeInMinutes(
    `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  );
  const currentDayIndex = getCurrentDayIndex();
  const currentDay = DAYS_ORDER[currentDayIndex];

  // Check if open now
  const todayHours = hours[currentDay];
  if (todayHours) {
    for (const range of todayHours) {
      const openMin = getTimeInMinutes(range.open);
      const closeMin = getTimeInMinutes(range.close);
      if (currentMinutes >= openMin && currentMinutes < closeMin) {
        return { status: 'open', closeTime: range.close };
      }
    }
  }

  // Find next opening time - check next 14 days
  for (let i = 0; i < 14; i++) {
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + i);
    const checkDayIndex = checkDate.getDay();
    const checkDay = DAYS_ORDER[checkDayIndex];
    const checkDayHours = hours[checkDay];

    if (checkDayHours && checkDayHours.length > 0) {
      if (i === 0) {
        // Today: find first slot that still opens in the future
        const futureSlot = checkDayHours.find(
          range => getTimeInMinutes(range.open) > currentMinutes
        );
        if (!futureSlot) continue; // All today's slots are already past
        return { status: 'closed', openDay: 'today', openTime: futureSlot.open };
      } else if (i === 1) {
        return { status: 'closed', openDay: 'tomorrow', openTime: checkDayHours[0].open };
      } else {
        return { status: 'closed', openDay: checkDay, openTime: checkDayHours[0].open };
      }
    }
  }

  return null;
}

export function formatHoursForDisplay(hours: Hours | undefined | null) {
  if (!hours || Object.keys(hours).length === 0) {
    return null;
  }

  return DAYS_ORDER.map((day) => {
    const dayHours = hours[day];

    if (!dayHours || dayHours.length === 0) {
      return {
        day,
        status: 'closed',
        ranges: [] as TimeRange[],
      };
    }

    return {
      day,
      status: 'open',
      ranges: dayHours,
    };
  });
}
